import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Building2,
  Clock,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  ExternalLink,
  Inbox,
} from "lucide-react";

const SERVICE_LABELS = {
  workforce: "Workforce Optimization",
  operations: "Operations Excellence",
  compliance: "Compliance & Risk",
  "patient-experience": "Patient Experience",
  emr: "EMR Implementation",
  growth: "Growth Strategy",
  carerevive: "CareRevive Program",
  foundations: "Foundations First",
  frontline: "Frontline Concierge",
  other: "Other",
};

const AdminContactSubmissions = () => {
  const { token, API } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, [page]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/admin/contacts?page=${page}&limit=25`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      } else {
        toast.error("Failed to load contacts");
      }
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const filtered = search
    ? contacts.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
          (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
          (c.company || "").toLowerCase().includes(search.toLowerCase()) ||
          (c.message || "").toLowerCase().includes(search.toLowerCase())
      )
    : contacts;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]" data-testid="contacts-page-title">
              Contact Submissions
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {total} total submission{total !== 1 ? "s" : ""} from the contact form
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="contacts-search"
            />
          </div>
        </div>

        {/* Contacts List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#2471A3]" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-16 text-gray-400">
              <Inbox className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">
                {search ? "No contacts match your search" : "No contact submissions yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((contact) => {
              const isExpanded = expanded === contact.contact_id;
              return (
                <Card
                  key={contact.contact_id}
                  className={`transition-shadow cursor-pointer ${
                    isExpanded ? "ring-2 ring-[#2471A3]/30 shadow-md" : "hover:shadow-md"
                  }`}
                  data-testid={`contact-card-${contact.contact_id}`}
                  onClick={() => setExpanded(isExpanded ? null : contact.contact_id)}
                >
                  <CardContent className="p-4 sm:p-5">
                    {/* Summary Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2471A3] to-[#1A2636] flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {(contact.name || "?").charAt(0).toUpperCase()}
                      </div>

                      {/* Name + Email */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1A2636] truncate">
                          {contact.name}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5 flex-wrap">
                          <a
                            href={`mailto:${contact.email}`}
                            className="flex items-center gap-1 hover:text-[#2471A3] transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`contact-email-link-${contact.contact_id}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                            {contact.email}
                          </a>
                          {contact.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {contact.phone}
                            </span>
                          )}
                          {contact.company && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5" />
                              {contact.company}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Service + Date */}
                      <div className="flex items-center gap-3 sm:text-right shrink-0">
                        {contact.service_interest && (
                          <Badge variant="secondary" className="text-xs whitespace-nowrap">
                            {SERVICE_LABELS[contact.service_interest] || contact.service_interest}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-400 whitespace-nowrap flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(contact.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Message Preview (always visible) */}
                    {contact.message && (
                      <div className={`mt-3 ${isExpanded ? "" : "pl-[52px]"}`}>
                        {!isExpanded ? (
                          <p className="text-sm text-gray-600 line-clamp-2" data-testid={`contact-message-preview-${contact.contact_id}`}>
                            <MessageSquare className="w-3.5 h-3.5 inline mr-1.5 text-gray-400 relative -top-px" />
                            {contact.message}
                          </p>
                        ) : null}
                      </div>
                    )}

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {contact.company && (
                            <div className="flex items-center gap-2 text-sm">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">Company:</span>
                              <span className="font-medium text-[#1A2636]">{contact.company}</span>
                            </div>
                          )}
                          {contact.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">Phone:</span>
                              <a href={`tel:${contact.phone}`} className="font-medium text-[#2471A3] hover:underline">
                                {contact.phone}
                              </a>
                            </div>
                          )}
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-600">Message</span>
                          </div>
                          <p className="text-sm text-[#1A2636] whitespace-pre-wrap leading-relaxed">
                            {contact.message}
                          </p>
                        </div>

                        <div className="flex gap-2 pt-1">
                          <a
                            href={`mailto:${contact.email}?subject=Re: Your inquiry to RNB Consulting Group`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button size="sm" className="bg-[#2471A3] hover:bg-[#2471A3]/90" data-testid={`reply-btn-${contact.contact_id}`}>
                              <Mail className="w-3.5 h-3.5 mr-1.5" />
                              Reply
                            </Button>
                          </a>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="contacts-prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="contacts-next-page"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminContactSubmissions;
